import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LanguageProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Feed from "@/pages/Feed";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RegisterAssociation from "@/pages/RegisterAssociation";
import AuthCallback from "@/pages/AuthCallback";
import TenantHome from "@/pages/TenantHome";
import TenantCalendar from "@/pages/TenantCalendar";
import TenantGallery from "@/pages/TenantGallery";
import EventDetail from "@/pages/EventDetail";
import MosqueDetail from "@/pages/MosqueDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import SuperAdmin from "@/pages/SuperAdmin";
import SyncHelp from "@/pages/SyncHelp";
import Privacy from "@/pages/Privacy";
import PushDebug from "@/pages/PushDebug";
import { PendingFollowHandler } from "@/components/PendingFollowHandler";

function HomeRouter() {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen bg-background" />;
  return user ? <Feed /> : <Landing />;
}

function RoleRouter() {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen bg-background" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/superadmin" replace />;
  if (["association_admin", "mosque_admin", "moderator"].includes(user.role)) return <Navigate to="/admin" replace />;
  return <Navigate to="/" replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<HomeRouter />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register-association" element={<RegisterAssociation />} />
      <Route path="/dashboard" element={<RoleRouter />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/superadmin" element={<SuperAdmin />} />
      <Route path="/push-debug" element={<PushDebug />} />
      <Route path="/aide-sync" element={<SyncHelp />} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route path="/:slug" element={<TenantHome />} />
      <Route path="/:slug/calendar" element={<TenantCalendar />} />
      <Route path="/:slug/gallery" element={<TenantGallery />} />
      <Route path="/:slug/events/:eventId" element={<EventDetail />} />
      <Route path="/:slug/mosques/:mosqueId" element={<MosqueDetail />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <PendingFollowHandler />
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
